import { Translatable } from '@fmnts/api/shared';
import { v1 as uuidv1 } from 'uuid';
import { FormModal } from './form-modal';
import { FormNavigation } from './form-navigation';
import { FormPage } from './form-page';
import { QuizField } from './quiz-field';
import { QuizFieldset } from './quiz-fieldset';

export interface IQuizPage {
  key: string;
  fieldsets: QuizFieldset[];
  headerImage: Translatable<string> | undefined;
  label: Translatable<string> | string | undefined;
  navigation: FormNavigation[];
  modals: readonly FormModal[];
}

export class QuizPage extends FormPage {
  override fieldsets: QuizFieldset[];
  headerImage: Translatable<string> | undefined;

  constructor(dto: Readonly<IQuizPage>) {
    super(dto);
    this.headerImage = dto.headerImage;
    this.fieldsets = dto.fieldsets;
  }
}

/**
 * Returns all fields withing any fieldset of that page
 */
export function getFieldsFromPage(page: QuizPage): QuizField[] {
  return page.fieldsets.map((fs) => [...fs.fields]).flat();
}

/**
 * Creates a new QuizPage from an array of QuizFields.
 *
 * @param fields Fields that should be saved within a QuizPage
 * Note: This will place all fields within a single QuizFieldset.
 *
 * @param opts Options can be passed to create fieldsets and pages,
 * e.g. keys for fieldsets and pages can be set using:
 * ```
 * QuizPage.fromFields(fields, {pageKey: 'page-0', fieldsetKey: 'fs-0'})
 * ```
 * Note: If no keys are passed, it will generate keys using uuid
 *
 * @returns QuizPage containing all fields
 */
export function createPageFromFields(
  fields: QuizField[],
  opts?: { pageKey?: string; fieldsetKey?: string },
): QuizPage {
  return new QuizPage({
    key: opts?.pageKey ?? uuidv1(),
    fieldsets: [
      new QuizFieldset({
        key: opts?.fieldsetKey ?? uuidv1(),
        fields,
        label: undefined,
      }),
    ],
    headerImage: undefined,
    label: undefined,
    navigation: [],
    modals: [],
  });
}
